import { boxShadowLevel2 } from '@issuu/ixu';
import {
    colorBaseGreyWarm50,
    colorTextLinkIndigoDefault,
    colorTextLinkIndigoFocus,
    colorTextLinkIndigoHover,
    colorTextLinkVioletDarkDefault,
    colorTextLinkVioletDarkFocus,
    colorTextLinkVioletDarkHover,
    layoutMarginMedium,
    ProductHeading,
    ProductParagraph,
    Spacing,
} from '@issuu/silkscreen';
import type { StreamItem } from 'product-document-page-shared';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styled, { css } from 'styled-components';

import { getStream } from '../../api';
import type { StreamName } from '../../types';
import { shouldShowAds } from '../../util/ads-utils';
import { isLayoutOneOf } from '../../util/layoutUtils';
import { AdsStream } from '../Ads/AdsStream';
import DocumentContext from '../DocumentPage/DocumentContext';
import type { GridLayout } from '../DocumentPage/useGridLayout';
import { LineClamp, TextOverflow } from '../shared/styles';
import { formatPrice } from './price-formatter';

const withAdItemCounts: Record<GridLayout, number> = {
    tiny: 4,
    small: 6,
    singleColumn: 2,
    twoColumn: 3,
    wideScreen: 4,
};

const fullItemCounts: Record<GridLayout, number> = {
    tiny: 4,
    small: 6,
    singleColumn: 5,
    twoColumn: 6,
    wideScreen: 7,
};

export const Root = styled.aside<{ streamName: string }>`
    margin: ${layoutMarginMedium} 0;
    width: 100%;
    grid-area: shelf-${({ streamName }) => streamName};
    &:first-child {
        margin-top: 0;
    }
    &:last-child {
        margin-bottom: 0;
    }
`;
const ShelfFrame = styled.div`
    width: 100%;
    min-height: 18rem;
    padding: ${layoutMarginMedium};
    background-color: ${colorBaseGreyWarm50};
`;

const CardList = styled.div<{ wrapItems: boolean; align: 'left' | 'center' | 'right' }>`
    display: flex;
    justify-content: ${({ align }) =>
        align === 'center' ? 'space-between' : align === 'right' ? 'flex-start' : 'flex-end'};
    align-items: baseline;
    ${({ wrapItems }) =>
        wrapItems
            ? css`
                  flex-wrap: wrap;
              `
            : ''}
`;

export const CardFrame = styled.div<{ siblings: number; isWrapped: boolean; isFirst: boolean }>`
    flex: ${({ siblings, isWrapped }) => `0 ${isWrapped ? '0' : '1'} ${99.5 / siblings}%`};
    order: ${({ isFirst }) => (isFirst ? 1 : 3)};
    padding-inline-end: ${layoutMarginMedium};
    min-width: 0;

    :last-child {
        padding-inline-end: 0;
    }
`;

const Link = styled.a`
    color: ${colorTextLinkIndigoDefault};
    :hover {
        color: ${colorTextLinkIndigoHover};
    }
    :active,
    :focus {
        color: ${colorTextLinkIndigoFocus};
    }

    :visited {
        color: ${colorTextLinkVioletDarkDefault};
    }
    :visited:hover {
        color: ${colorTextLinkVioletDarkHover};
    }
    :visited:active,
    :visited:focus {
        color: ${colorTextLinkVioletDarkFocus};
    }
`;

export const DocumentLink = styled(Link)`
    display: block;
`;

const AspectRatio = styled.div<{ ratio: number }>`
    aspect-ratio: ${({ ratio }) => ratio};

    ::before {
        content: '';
        width: 1px;
        margin-left: -1px;
        float: left;
        height: 0;
        padding-top: calc(${({ ratio }) => 1 / ratio} * 100%);
    }
    ::after {
        content: '';
        display: table;
        clear: both;
    }
`;

const CoverFrame = styled.div`
    position: relative;
    height: 100%;
    box-shadow: ${boxShadowLevel2};
    transition: box-shadow 0.15s;

    ::before {
        background-image: linear-gradient(
            -90deg,
            transparent 4%,
            hsla(0, 0%, 100%, 0.2) 61%,
            hsla(0, 0%, 100%, 0.4) 85%,
            hsla(0, 0%, 74%, 0) 96%
        );
        content: '';
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0.77;
        pointer-events: none;
        position: absolute;
        transition: opacity 0.15s;
    }
`;

const CoverImage = styled.img`
    width: 100%;
    height: 100%;
`;

const createLinkUrl = (item: StreamItem) => `/${item.ownerUsername}/docs/${item.publicationName}`;

const createImageUrl = (imageHost: string, { revisionId, publicationId }: StreamItem) =>
    `https://${imageHost}/${revisionId}-${publicationId}/jpg/page_1_thumb_large.jpg`;

type CardProps = {
    item: StreamItem;
    imageHost: string;
    siblings: number;
    isWrapped: boolean;
    isFirst?: boolean;
};

const Card = ({ imageHost, item, siblings, isWrapped, isFirst = false }: CardProps) => {
    const {
        trackers: { website: websiteTracker },
    } = useContext(DocumentContext);
    const { coverRatio, ownerUsername, price, title, revisionId, publicationId, publicationName } =
        item;
    const [cardRef, inView] = useInView({ triggerOnce: true });
    const itemTrackingData = {
        doc_id: `${revisionId}-${publicationId}`,
        doc_creator: ownerUsername,
        doc_name: publicationName,
        stream_ranking: 0, // Unfortunately, this field is both deprecated and required
    };
    useEffect(() => {
        if (inView) {
            websiteTracker({
                type: 'document_impression',
                page: 1,
                ...itemTrackingData,
            });
        }
    }, [inView]);
    return (
        <CardFrame isFirst={isFirst} isWrapped={isWrapped} ref={cardRef} siblings={siblings}>
            <DocumentLink
                href={createLinkUrl(item)}
                onClick={event => {
                    event.preventDefault();
                    websiteTracker({ type: 'document_click', ...itemTrackingData }).finally(() => {
                        window.location.href = createLinkUrl(item);
                    });
                }}
            >
                <AspectRatio ratio={coverRatio}>
                    <CoverFrame>
                        <CoverImage
                            alt=""
                            role="presentation"
                            src={createImageUrl(imageHost, item)}
                        />
                    </CoverFrame>
                </AspectRatio>
                <LineClamp lines={3}>
                    <ProductParagraph step="negative-1">{title}</ProductParagraph>
                </LineClamp>
            </DocumentLink>
            <TextOverflow>
                <ProductParagraph step="negative-2">
                    by <Link href={`/${ownerUsername}`}>{ownerUsername}</Link>
                </ProductParagraph>
            </TextOverflow>
            {price ? <ProductParagraph step="0">{formatPrice(price)}</ProductParagraph> : null}
        </CardFrame>
    );
};

const streamNameToHeadline = (streamName: StreamName): string =>
    // Stream names are currently kebab-cased plain English headlines
    streamName.replace(/-/g, ' ').replace(/^\w/, m => m.toUpperCase());

const Headline = ({ streamName }: { streamName: StreamName }) => (
    <Spacing bottom={2}>
        <ProductHeading level={2} step="negative-2">
            {streamNameToHeadline(streamName)}
        </ProductHeading>
    </Spacing>
);

type StreamCardsProps = {
    isShowingAds: boolean;
    layout: GridLayout;
    streamName: StreamName;
    items: StreamItem[];
    cardCount: number;
    imageHost: string;
};
const StreamCards = ({
    isShowingAds,
    layout,
    streamName,
    imageHost,
    items,
    cardCount,
}: StreamCardsProps) => {
    const isWrapped = isLayoutOneOf(['tiny', 'small'], layout);
    const firstItems = items.slice(0, cardCount / 2);
    const lastItems = items.slice(cardCount / 2);
    const siblings = isWrapped ? cardCount / 2 : fullItemCounts[layout];
    return (
        <CardList
            align={isWrapped ? 'center' : streamName === 'popular-now' ? 'right' : 'left'}
            data-testid={`card-list-${streamName}`}
            wrapItems={isWrapped}
        >
            {firstItems.map(item => (
                <Card
                    imageHost={imageHost}
                    isFirst
                    isWrapped={isWrapped}
                    item={item}
                    key={item.publicationId}
                    siblings={siblings}
                />
            ))}
            {lastItems.map(item => (
                <Card
                    imageHost={imageHost}
                    isWrapped={isWrapped}
                    item={item}
                    key={item.publicationId}
                    siblings={siblings}
                />
            ))}
            {isShowingAds ? <AdsStream streamName={streamName} wrapped={isWrapped} /> : null}
        </CardList>
    );
};

export type Props = {
    streamName: StreamName;
};

export const StreamShelf: React.FC<Props> = ({ streamName }: Props) => {
    const context = useContext(DocumentContext);
    const {
        document: { documentName },
        hosts: { image: imageHost },
        publisher: { username: publisherName },
        appState: {
            currentLayout: { name: layout },
        },
    } = context;
    const isMounted = useRef(false);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    });
    const [allItems, setAllItems] = useState<StreamItem[]>([]);
    const [loadingStream, setLoadingStream] = useState(false);
    // Start to initialize the stream shelf, when its position is less than
    // 400px off the viewport, so that it can have a better chance to preload
    // the thumbnails before visitor can see them. This patch will impact the
    // visits on document page, that come from document page
    const [viewRef, inView] = useInView({ triggerOnce: true, rootMargin: '400px' });
    useEffect(() => {
        if (inView && !loadingStream && allItems.length < 1) {
            setLoadingStream(true);
            getStream(streamName, publisherName, documentName)
                .then(streamResponse => {
                    isMounted.current && setAllItems(streamResponse.items);
                })
                .catch(() => {
                    // Our bff relay the requests to the legacy stream servers,
                    // which unfortunately times out a lot. And I'm afraid there
                    // is nothing we can do. Therefore, ignore them to sentry
                })
                .finally(() => {
                    isMounted.current && setLoadingStream(false);
                });
        }
    }, [allItems, inView]);
    const isShowingAds = shouldShowAds({ context, ignoreLicense: true });
    const cardCount =
        streamName === 'read-more' ? fullItemCounts[layout] : withAdItemCounts[layout];
    const items = allItems.slice(0, cardCount);
    return (
        <Root aria-label={streamNameToHeadline(streamName)} ref={viewRef} streamName={streamName}>
            <ShelfFrame>
                <Headline streamName={streamName} />
                <StreamCards
                    cardCount={cardCount}
                    imageHost={imageHost}
                    isShowingAds={isShowingAds}
                    items={items}
                    layout={layout}
                    streamName={streamName}
                />
            </ShelfFrame>
        </Root>
    );
};
